import axios from "axios";
import { reportClientError } from "@/lib/errorReporter";

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

// Add auth token to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Public routes — pages that should NEVER force a login redirect even if the
// stored refresh token is expired. These are PDF-link landing pages, embedded
// quote tools, and shareable client/referrer portals. Without this list, a
// user with a stale refresh token who clicks a PDF Instruct hyperlink ends up
// bounced to /login instead of seeing the public quote.
const PUBLIC_ROUTE_PREFIXES = [
  "/login",
  "/quote",         // /quote, /quote/referrer, /quote/view/{id}
  "/portal/",
  "/sign/",
  "/referrer/",
  "/referrer-dashboard/",
  "/referrers-web-tool",
];

function isOnPublicRoute() {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return PUBLIC_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(p));
}

// Handle 401 responses — try refresh with a SINGLE-FLIGHT pattern so
// concurrent requests don't each fire their own refresh and race on the
// stored token. Historically we let every 401'd request kick off its own
// refresh; when 5–6 dashboard calls hit an expired token at the same
// moment, one refresh would succeed and others could fail transiently
// (network jitter / backend rate limit) — the failing handler would
// then `window.location.href = "/login"` and hard-reload the app while
// the successful ones had already re-authorised. Users described this
// as "dashboard shows for a second then keeps loading" — exactly the
// spinner returned by ProtectedRoute after the forced reload. The
// fix below lets ONLY the first 401 fire the refresh; the rest queue
// and retry with whatever token that single refresh produces.
let _refreshInFlight = null; // Promise resolving to a fresh access_token

async function _sharedRefresh() {
  if (_refreshInFlight) return _refreshInFlight;
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    return Promise.reject(new Error("no_refresh_token"));
  }
  _refreshInFlight = axios
    .post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {
      refresh_token: refreshToken,
    })
    .then(({ data }) => {
      localStorage.setItem("access_token", data.access_token);
      // Backend currently only returns access_token; guard for future
      // rotation without breaking.
      if (data.refresh_token) {
        localStorage.setItem("refresh_token", data.refresh_token);
      }
      return data.access_token;
    })
    .finally(() => {
      // Clear the in-flight slot BEFORE waiters resolve so any late
      // 401 that arrives during retry gets a fresh single-flight
      // cycle instead of latching onto the stale promise.
      _refreshInFlight = null;
    });
  return _refreshInFlight;
}

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Normalise FastAPI 422 validation errors — their `detail` is an ARRAY of
    // {msg,loc,...} objects. Components commonly do toast.error(e.response.data.detail),
    // and passing an array straight to React/sonner throws "Objects are not valid
    // as a React child" and trips the page-level error boundary. Collapsing the
    // array to a readable string here fixes it everywhere at once. Structured
    // object details (e.g. the 429 nudge {message, retry_after_seconds}) are left
    // untouched so their consumers keep working.
    if (Array.isArray(error.response?.data?.detail)) {
      error.response.data.detail = formatApiErrorDetail(error.response.data.detail);
    }
    if (error.response?.status !== 401 || error.config?._retry) {
      return Promise.reject(error);
    }
    error.config._retry = true;
    try {
      const newAccessToken = await _sharedRefresh();
      error.config.headers.Authorization = `Bearer ${newAccessToken}`;
      return API(error.config);
    } catch (refreshErr) {
      // Refresh genuinely failed — clear tokens and (only on protected
      // pages) bounce to /login. Public routes just let the 401 bubble.
      // Report the failure to the backend so we can see WHY refresh
      // died on the affected user (this is exactly what we were
      // guessing at before).
      try {
        reportClientError({
          kind: "auth.refresh_failed",
          message: refreshErr?.message || String(refreshErr),
          original_request_url: error.config?.url || "",
          on_public_route: isOnPublicRoute(),
        });
      } catch { /* reporter must not itself throw */ }
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      if (!isOnPublicRoute()) {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Download a protected file through the authenticated axios instance.
// Plain <a href> links can't send the Bearer token, so hitting a
// download/pack endpoint directly returns {"detail":"Not authenticated"}.
// This fetches the bytes as a blob (token attached + auto-refresh) and
// triggers a client-side download. `path` is relative to /api.
export async function downloadAuthed(path, filename) {
  const resp = await API.get(path, { responseType: "blob" });
  const type = resp.headers?.["content-type"] || "application/octet-stream";
  const blob = new Blob([resp.data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default API;
